import React, { useCallback } from 'react';

import {
    postMessageRN,
    runningInRNWebView,
    useReactNativeProps,
    WebToRNAction,
} from '@eika-infrastruktur/mobilbank-lib';
import { AttentionMessage } from '@eika/attention-message';
import { Button } from '@eika/button';
import { IconTypes } from '@eika/icon';

import { LastLoginLabel } from '../../components/LastLoginLabel';
import { useConfig } from '../../defaultConfig';
import { useMultipleAgreements } from '../../fetchHooks/useProfile';
import useUnreadMessages from '../../fetchHooks/useUnreadMessages';
import useTranslation, { formatJsx } from '../../hooks/useTranslation';
import { settingsTrackIDs } from '../../utils/gtm/trackIDs/settings';
import CounterButton from './CounterButton';
import SwitchBankOrAgreementButton from './SwitchBankOrAgreementButton';
import UserInfo from './UserInfo';

import './UserProfile.scss';

const UserProfile: React.FC = () => {
    const { nettpostLink, messagesLink, featureToggles } = useConfig();
    const translations = useTranslation();

    const messageInfo = useUnreadMessages();
    const { bankRelationships } = useReactNativeProps();
    const hasSeveralBanks = bankRelationships && bankRelationships?.length > 1;
    const { data: hasUserAgreements, isLoading: isLoadingAgreements, isError } = useMultipleAgreements();

    const handleSwitchBank = useCallback(() => {
        postMessageRN({ action: WebToRNAction.SWITCH_BANK, payload: undefined });
    }, []);

    const logout = () =>
        runningInRNWebView
            ? postMessageRN({
                  action: WebToRNAction.LOGOUT,
                  payload: { automaticLogout: false },
              })
            : null;

    return (
        <div className="user-profile">
            <div className="user-profile__header">
                <UserInfo />
                <LastLoginLabel />
                {window.ReactNativeWebView && (
                    <div className="user-profile__action-button-wrapper">
                        <SwitchBankOrAgreementButton
                            hasUserAgreements={hasUserAgreements?.hasAgreements === true}
                            hasSeveralBanks={!!hasSeveralBanks}
                            handleSwitchBank={handleSwitchBank}
                            isLoading={isLoadingAgreements}
                            isError={isError && !hasSeveralBanks}
                            dataTrackId={settingsTrackIDs.userLink}
                        />
                        <Button onClick={logout}>{translations.logout}</Button>
                    </div>
                )}
            </div>

            {featureToggles.showOldSystemDeletionAttentionMessage && (
                <AttentionMessage
                    className="user-profile-attention-message"
                    icon={IconTypes.UTROPSTEGN_24}
                    type="attention"
                    text={
                        <span className="user-profile-attention-message__content">
                            <span className="user-profile-attention-message__title bold">
                                {translations.messages.oldMessagesWillBeRemoved}
                            </span>
                            <span className="user-profile-attention-message__description">
                                {formatJsx(
                                    translations.messages.ifYouWishToSaveThem,
                                    <span className="user-profile-attention-message__description--italic">
                                        {translations.messages.messagesText}
                                    </span>,
                                )}
                            </span>
                        </span>
                    }
                />
            )}
            <div className="user-profile__button-wrapper">
                {featureToggles.enableSecureCommunication && messagesLink?.url && (
                    <CounterButton
                        count={messageInfo.data?.numberOfUnreadMessages}
                        href={messagesLink.url}
                        icon={IconTypes.CHAT_36}
                        label={translations.profile.messages}
                        isLoading={messageInfo.isLoading}
                        dataTrackId={settingsTrackIDs.communicationLink}
                    />
                )}
                {nettpostLink?.url && (
                    <CounterButton
                        count={messageInfo.data?.numberOfUnreadLetters}
                        href={nettpostLink.url}
                        icon={IconTypes.MAIL_36}
                        label={translations.profile.letters}
                        isLoading={messageInfo.isLoading}
                        dataTrackId={settingsTrackIDs.communicationLink}
                    />
                )}
            </div>
        </div>
    );
};

export default UserProfile;
